// Vendors
import { get, kebabCase } from 'lodash'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import React, { useMemo, useCallback, useEffect } from 'react'

// Functions
import { useCan } from 'hooks/useCan'
import { formatDate } from 'utils/formatDate'
import formatCurrency from 'utils/formatCurrency'
import { useListRecords } from 'hooks/useListRecords'
import { useSelectParser } from 'hooks/useSelectParser'

// Components
import { ListTable } from 'components/ListTable'
import { ListFilters } from 'components/ListFilters'
import { ListActions } from 'components/ListActions'
import { MenuExports } from 'components/MenuExports'
import { ListPagination } from 'components/ListPagination'
import { ListTableRenderAs } from 'components/ListTableRowComponent'

// Styles & Icons
import { Select, Stack } from '@chakra-ui/react'
import { LayoutLoggedPageList } from 'layout/PrivatePageList'

// Types
import { PageMetaSetting } from './meta'
import { Record } from 'types/Requests'
import { PurchaseHasProductType } from '../show/types'
import { RULES } from './rules'
import { FieldDateRangePicker } from 'components/FieldDateRangePicker'

enum PurchaseTypeEnum {
  B2C = 'B2C',
  B2B = 'B2B',
  LEGACY = 'LEGADO',
  RECOLLECT = 'RECOLETA',
}

type ListRecords = Record & {
  price: string
  status: string | null
  purchaseNumber: string
  purchaseDeliveryId: string
  deliveryTrackingCode: string
  paymentMethodName: string
  shippingMethodName: string
  shippingValue: string
  purchaseHasProducts: PurchaseHasProductType[]
  kitsAmount: string
  kitsName: string
  createdAtFormatted: string
  priceFormatted: string
  isCobranding: boolean
  purchaseType: keyof typeof PurchaseTypeEnum | null
  purchaseTypeFormatted: PurchaseTypeEnum | null
  purchaseOrigin: 'pdv' | 'store' | 'testfy' | null
  purchaseStatus: string
  isCobrandingFormatted: string
  invoiceUniqueNumber: string | null
  buyerName: string | null
  clinicName: string | null
}

export const PageOrdersListRecords = (): JSX.Element => {
  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */

  const selectVoucherCode = useSelectParser({
    accessor: { value: 'voucherCode', label: 'voucherCode' },
    endpoint: '/app/coupon',
  })

  const selectSeller = useSelectParser({
    accessor: { value: 'userId', label: 'name' },
    endpoint: '/app/person?type=access-order-module',
  })

  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */

  const history = useHistory()
  const { t } = useTranslation('common')

  const { can: canEdit } = useCan({ rules: RULES })

  const { endpoint, title } = PageMetaSetting
  const {
    records: Records,
    query: Query,
    pagination: Pagination,
  } = useListRecords<ListRecords>({
    endpoint: endpoint,
    query: {
      startDate: undefined,
      endDate: undefined,
      sort: '-createdAt',
    },
  })

  /*
  |-----------------------------------------------------------------------------
  | Effects
  |-----------------------------------------------------------------------------
  |
  |
  */

  useEffect(() => {
    Records.list.forEach((record) => {
      if (!record.status) {
        record.status = record.purchaseStatus.toUpperCase()
      }

      if (record.purchaseStatus.toUpperCase() === 'REFUSED') {
        record.status = record.purchaseStatus.toUpperCase()
      }

      if (kebabCase(record.paymentMethodName) === 'credit-card') {
        record.paymentMethodName = 'Cartão de crédito'
      }

      record.isCobrandingFormatted = record.isCobranding ? 'COBRANDING' : '-'
      record.buyerName = record.clinicName || record.buyerName
      record.kitsAmount = String(record.purchaseHasProducts.length)

      const purchaseType = get(record, 'purchaseType')
      if (purchaseType) {
        record.purchaseTypeFormatted = get(PurchaseTypeEnum, purchaseType)
      }

      record.createdAtFormatted = formatDate(record.createdAt, 'dd/MM/yyyy')
      record.priceFormatted = formatCurrency(+record.price)
    })
  }, [Records.list])

  /*
  |-----------------------------------------------------------------------------
  | Memos
  |-----------------------------------------------------------------------------
  |
  |
  */

  const ListTableHeaders = useMemo(
    () => [
      {
        label: 'Cód. pedido',
        accessor: 'purchaseNumber',
      },
      {
        label: 'Comprador',
        accessor: 'buyerName',
      },
      {
        label: 'Data da compra',
        accessor: 'createdAtFormatted',
      },
      {
        label: 'QTD',
        accessor: 'kitsAmount',
        render: { as: ListTableRenderAs.MULTILINE_TEXT },
      },
      {
        label: 'Total R$',
        accessor: 'priceFormatted',
      },
      {
        label: t('list_table.headers.status'),
        accessor: 'status',
        render: { as: ListTableRenderAs.BADGE },
      },
      {
        label: 'Forma de envio',
        accessor: 'shippingMethodName',
      },
      {
        label: 'Forma de pagamento',
        accessor: 'paymentMethodName',
      },
      {
        label: 'Origem',
        accessor: 'purchaseOrigin',
        render: {
          as: ListTableRenderAs.TEXT,
          options: {
            textTransform: 'uppercase',
          },
        },
      },
      {
        label: 'Tipo de pedido',
        accessor: 'purchaseTypeFormatted',
        render: {
          as: ListTableRenderAs.TEXT,
          options: {
            textTransform: 'uppercase',
          },
        },
      },
      {
        label: '',
        accessor: 'isCobrandingFormatted',
        render: {
          as: ListTableRenderAs.TEXT,
          options: {
            textTransform: 'uppercase',
          },
        },
      },
    ],
    [t]
  )

  /*
  |-----------------------------------------------------------------------------
  | Functions
  |-----------------------------------------------------------------------------
  |
  |
  */

  const transactionDetails = useCallback((_, record) => {
    const { paymentProviderTransactionCode } = record
    window
      .open(
        `https://beta.dashboard.sandbox.pagar.me/#/transactions/${paymentProviderTransactionCode}`,
        'blank'
      )
      ?.focus()
  }, [])

  const listTableRowAction = useCallback(
    (Record) => {
      if (!Record) return
      history.push(`/orders/${Record.id}`)
    },
    [history]
  )

  const fnTableActionProps = useCallback((record: any) => {
    return {
      bg: record.isCobranding
        ? 'linear-gradient(90deg, rgba(253, 201, 11, 0.144) 0.01%, rgba(254, 223, 110, 0.207217) 0.06%, rgba(255, 255, 255, 0.3) 5%);'
        : '',
    }
  }, [])

  /*
  |-----------------------------------------------------------------------------
  | Renders
  |-----------------------------------------------------------------------------
  |
  |
  */
  return (
    <LayoutLoggedPageList title={title}>
      <Stack>
        <Stack
          spacing="4"
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
        >
          <ListFilters query={Query}>
            <FieldDateRangePicker
              isSmall
              name="filterDate"
              placeholderText="Filtrar por data"
              containerProps={{
                marginBottom: '0',
              }}
              showMonthDropdown
              dropdownMode="select"
              query={Query}
            />
          </ListFilters>

          <ListActions>
            <MenuExports endpoint={endpoint} query={Query.current} />
          </ListActions>
        </Stack>

        <Stack
          overflowX="auto"
          direction="row"
          sx={{ select: { minWidth: '64' } }}
          spacing={{ base: '1', lg: '4' }}
        >
          <Select
            size="sm"
            placeholder="Todos os status logísticos"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              Query.set({
                ...Query.current,
                status: e.target.value,
              })
            }}
          >
            <option value="IN_EXPEDITION">Em expedição</option>
            <option value="SENT">Enviado</option>
            <option value="SHIPPING_ALERT">Alerta de envio</option>

            {canEdit.inTransit && (
              <option value="IN_TRANSIT">Em trânsito</option>
            )}
            {canEdit.sent && <option value="DELIVERED">Entregue</option>}
            {canEdit.sent && <option value="CANCELED">Cancelado</option>}
          </Select>

          <Select
            size="sm"
            placeholder="Todos os status financeiro"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              Query.set({
                ...Query.current,
                purchaseStatus: e.target.value,
              })
            }}
          >
            <option value="waiting_payment">Aguardando pagamento</option>
            <option value="processing">Em processamento</option>
            <option value="authorized">Autorizado</option>
            <option value="paid">Pagamento aprovado</option>
            <option value="refunded">Devolveu</option>
            <option value="pending_refunded">Reembolso pendente</option>
            <option value="refused">Recusou</option>
            <option value="canceled">Cancelado</option>
          </Select>

          <Select
            size="sm"
            placeholder="Todos os tipos de compra"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              Query.set({
                ...Query.current,
                purchaseType: e.target.value,
              })
            }}
          >
            <option value="B2C">B2C</option>
            <option value="B2B">B2B</option>
            <option value="LEGACY">LEGADO</option>
          </Select>

          <Select
            size="sm"
            placeholder="Cobranding e não cobranding"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              Query.set({
                ...Query.current,
                isCobranding: e.target.value === 'isCobranding',
              })
            }}
          >
            <option value="isCobranding">É cobranding</option>
            <option value="notIsCobranding">Não é cobranding</option>
          </Select>

          <Select
            size="sm"
            placeholder="Todos os coupons"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              Query.set({ ...Query.current, voucherCode: e.target.value })
            }
          >
            {selectVoucherCode.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            size="sm"
            placeholder="Todos os vendedores"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              Query.set({ ...Query.current, sellerId: e.target.value })
            }
          >
            {selectSeller.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Stack>
      </Stack>

      <ListTable
        headers={ListTableHeaders}
        records={Records}
        rowAction={listTableRowAction}
        menuButtonProps={{ variant: 'ghost' }}
        defaultActions="none"
        trProps={fnTableActionProps}
        actions={[
          {
            type: 'alterPurchaseStatus',
            options: {
              reload: Records.reload,
            },
          },
          {
            type: 'alterCobrandingStatus',
            options: {
              reload: Records.reload,
            },
          },
          {
            type: 'generateNewPaymentLink',
            options: {
              showWhen: (record) =>
                (record.status === 'WAITING_PAYMENT' ||
                  record.status === 'REFUSED') &&
                record.paymentMethodName === 'Link de Pagamento',
            },
          },
          {
            type: 'authorizeShipment',
            options: {
              reload: Records.reload,
              showWhen: ({ status }) => {
                return status === 'WAITING_PAYMENT' || status === 'PAID'
              },
            },
          },
          {
            type: 'pagarme',
            options: {
              callback: transactionDetails,
            },
          },
          {
            type: 'cancelOrder',
            options: {
              reload: Records.reload,
            },
          },
        ]}
      />

      <ListPagination pagination={Pagination} query={Query} />
    </LayoutLoggedPageList>
  )
}
